import { logError } from '@/lib/sentry.lib';

import { useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

import { isDocuSignEnabled } from '@/feature/docusign.feature';
import { useSignaturesIndexStore } from '@/store/docusign-modal.store';
import {
    selectAssetCreationDate,
    selectAssetStatus,
    selectOrderType,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import { useAccountTemplates, useCreateSignRequestMutation, useSignRequests } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { assetStatusTypes } from '@repo/types/asset';
import { getNotifier } from '@repo/utils';

import type { CreateSignParams } from '@repo/api-adapters/docusign';
import { getServiceBaseUrl } from '@/api/helpers.api';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { analyticsEvents, trackAnalyticsEvent } from '@/utils/analytics.utils';
import { getDocusignFromDateValue, isValidEmail } from '@/utils/docusign.utils';
import { getAppConfig } from '@/utils/window.utils';
interface UseSignRequestsSummaryResult {
    summaryText: string;
}

export const useSignRequestsSummary = (): UseSignRequestsSummaryResult => {
    const { assetId, assetType } = useAssetParams();
    const { data: assetData } = useAsset(assetType, assetId);
    const config = getAppConfig();
    const basePath = getServiceBaseUrl(config.api.services.orderTaking, 'docusign');

    const creationDate = selectAssetCreationDate(assetData);
    const { data = [] } = useSignRequests({
        orderId: assetId,
        fromDate: getDocusignFromDateValue(creationDate),
        basePath: basePath,
        enabled:
            Boolean(creationDate) &&
            isDocuSignEnabled({
                assetType,
                orderType: selectOrderType(assetData),
                assetStatus: selectAssetStatus(assetData),
            }),
    });

    const summaryText = useMemo(() => {
        const total = data.length;
        if (total === 0) {
            return translate('docusign_no_signature_requested');
        }

        const signed = data.filter((row) => row.status?.toUpperCase() === 'COMPLETED').length;

        return translate('docusign_documents_signed', {
            signed: signed.toString(),
            total: total.toString(),
        });
    }, [data]);

    return { summaryText };
};

export const useSignatureRequestMutation = () => {
    const { email, setMode, selectedRequestTemplates, mode } = useSignaturesIndexStore(
        useShallow((state) => ({
            email: state.email,
            setMode: state.setMode,
            selectedRequestTemplates: state.selectedRequestTemplates,
            mode: state.mode,
        }))
    );
    const { assetId, assetType } = useAssetParams();
    const { data: assetData } = useAsset(assetType, assetId);
    const assetStatus = selectAssetStatus(assetData);
    const isCancelled = assetStatus === assetStatusTypes.CANCELLED || assetStatus === assetStatusTypes.CANCELED;

    const basePath = getServiceBaseUrl(getAppConfig().api.services.orderTaking, 'docusign');

    const { mutateAsync, isPending } = useCreateSignRequestMutation(basePath);

    const { data: accountTemplatesResult, hasError } = useAccountTemplates({
        basePath,
        enabled:
            isDocuSignEnabled({
                assetType,
                orderType: selectOrderType(assetData),
                assetStatus,
            }) && mode === 'new',
    });

    const canSubmit =
        !isPending && isValidEmail(email) && selectedRequestTemplates.length > 0 && !hasError && !isCancelled;

    const submit = async (appendixFiles: File[]): Promise<boolean> => {
        if (!canSubmit) {
            return false;
        }

        try {
            await Promise.all(
                selectedRequestTemplates.map(async (templateId) => {
                    const foundTemplate = accountTemplatesResult?.accountTemplates?.find(
                        (template) => String(template.templateId) === templateId
                    );
                    const templateName = (foundTemplate?.templateName ?? templateId).trim();

                    const payload: CreateSignParams = {
                        data: {
                            referenceNumber: assetId,
                            templateId,
                            emailSubject: translate('docusign_email_subject', {
                                assetId: assetId,
                            }),
                            receivers: [
                                {
                                    roleName: 'Signer1', // it is expected to be Signer1 because the template is created with that role - XDK-168
                                    email: email.trim(),
                                    name: email.trim(),
                                    routingOrder: 1, // it is expected to be 1 because right now since we only send docusign document to one signer
                                },
                            ],
                            metadata: [
                                { name: 'templateId', show: false, value: templateId },
                                { name: 'templateName', show: false, value: templateName },
                            ],
                        },
                        appendix: appendixFiles,
                    };

                    await mutateAsync(payload);
                })
            );

            trackAnalyticsEvent({
                eventName: analyticsEvents.docusignSignatureRequestSent,
                custom: {
                    assetId,
                    hasAppendix: appendixFiles.length > 0,
                },
            });
            getNotifier()?.add({
                title: translate('orderview_signatures_request_sent'),
                transient: true,
            });
            setMode('status');
            return true;
        } catch (error) {
            logError(error);
            getNotifier()?.add({
                title: translate('orderview_signatures_request_failed'),
                type: 'error',
            });
            return false;
        }
    };
    return { canSubmit, submit, isPending, isCancelled };
};
